import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ColorLens as ColorLensIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNotification } from '../../contexts/NotificationContext';
import especialidadService from '../../services/especialidadService';
import PageHeader from '../../components/common/PageHeader';
import DeleteConfirmDialog from '../../components/common/DeleteConfirmDialog';
import { useAuth } from '../../hooks/useAuth';

const Especialidades = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [especialidades, setEspecialidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEspecialidad, setSelectedEspecialidad] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isAdmin = user?.rol === 'Administrador';

  // Cargar especialidades
  useEffect(() => {
    const fetchEspecialidades = async () => {
      setLoading(true);
      try {
        const response = await especialidadService.getEspecialidades();
        setEspecialidades(response.data);
      } catch (error) {
        console.error('Error fetching especialidades:', error);
        showNotification('Error al cargar las especialidades', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchEspecialidades();
  }, [refreshKey, showNotification]);

  // Esquema de validación con Yup
  const validationSchema = Yup.object({
    nombre: Yup.string().required('El nombre es requerido'),
    color: Yup.string()
      .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, 'Ingrese un color hexadecimal válido')
      .required('El color es requerido'),
    duracion_cita_defecto: Yup.number()
      .min(15, 'La duración mínima es de 15 minutos')
      .max(180, 'La duración máxima es de 180 minutos')
      .required('La duración por defecto es requerida'),
    tarifa_por_defecto: Yup.number()
      .min(0, 'La tarifa no puede ser negativa')
      .required('La tarifa por defecto es requerida'),
  });

  // Formik para manejar el formulario
  const formik = useFormik({
    initialValues: {
      nombre: '',
      descripcion: '',
      color: '#5D1390',
      duracion_cita_defecto: 60,
      tarifa_por_defecto: 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (editMode) {
          await especialidadService.updateEspecialidad(selectedEspecialidad.id, values);
          showNotification('Especialidad actualizada con éxito', 'success');
        } else {
          await especialidadService.createEspecialidad(values);
          showNotification('Especialidad creada con éxito', 'success');
        }
        setDialogOpen(false);
        setRefreshKey(old => old + 1); // Refrescar lista
      } catch (error) {
        console.error('Error saving especialidad:', error);
        showNotification(
          `Error al ${editMode ? 'actualizar' : 'crear'} la especialidad`,
          'error'
        );
      }
    },
  });

  // Abrir diálogo de creación
  const handleCreateClick = () => {
    setEditMode(false);
    formik.resetForm();
    setDialogOpen(true);
  };

  // Abrir diálogo de edición
  const handleEditClick = (especialidad) => {
    setEditMode(true);
    setSelectedEspecialidad(especialidad);
    formik.setValues({
      nombre: especialidad.nombre,
      descripcion: especialidad.descripcion || '',
      color: especialidad.color,
      duracion_cita_defecto: especialidad.duracion_cita_defecto,
      tarifa_por_defecto: especialidad.tarifa_por_defecto,
    });
    setDialogOpen(true);
  };

  // Cerrar diálogo
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Abrir diálogo de confirmación para eliminar
  const handleDeleteClick = (especialidad) => {
    setSelectedEspecialidad(especialidad);
    setDeleteDialogOpen(true);
  };

  // Confirmar eliminación de especialidad
  const handleDeleteConfirm = async () => {
    try {
      await especialidadService.deleteEspecialidad(selectedEspecialidad.id);
      showNotification('Especialidad eliminada correctamente', 'success');
      setRefreshKey(old => old + 1); // Refrescar lista
    } catch (error) {
      console.error('Error deleting especialidad:', error);
      showNotification('Error al eliminar la especialidad', 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="Especialidades"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Configuración', to: '/configuracion' },
          { text: 'Especialidades' }
        ]}
        actionButton={
          isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            >
              Nueva Especialidad
            </Button>
          )
        }
      />

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : especialidades.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No se encontraron especialidades registradas.
              </Typography>
              {isAdmin && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                  sx={{ mt: 2 }}
                >
                  Crear Nueva Especialidad
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Color</TableCell>
                    <TableCell>Duración por Defecto</TableCell>
                    <TableCell>Tarifa por Defecto</TableCell>
                    {isAdmin && <TableCell align="right">Acciones</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {especialidades.map((especialidad) => (
                    <TableRow key={especialidad.id}>
                      <TableCell>{especialidad.nombre}</TableCell>
                      <TableCell>{especialidad.descripcion || '-'}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: especialidad.color,
                              mr: 1,
                              border: '1px solid #ddd'
                            }}
                          />
                          {especialidad.color}
                        </Box>
                      </TableCell>
                      <TableCell>{especialidad.duracion_cita_defecto} minutos</TableCell>
                      <TableCell>S/ {(Number(especialidad.tarifa_por_defecto) || 0).toFixed(2)}</TableCell>
                      {isAdmin && (
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditClick(especialidad)}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(especialidad)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Diálogo para crear/editar especialidad */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {editMode ? 'Editar Especialidad' : 'Nueva Especialidad'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="nombre"
                  name="nombre"
                  label="Nombre *"
                  value={formik.values.nombre}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.nombre && Boolean(formik.errors.nombre)}
                  helperText={formik.touched.nombre && formik.errors.nombre}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="descripcion"
                  name="descripcion"
                  label="Descripción"
                  value={formik.values.descripcion}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="color"
                  name="color"
                  label="Color *"
                  value={formik.values.color}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.color && Boolean(formik.errors.color)}
                  helperText={formik.touched.color && formik.errors.color}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: formik.values.color,
                          mr: 1,
                          border: '1px solid #ddd'
                        }}
                      />
                    ),
                    endAdornment: (
                      <Tooltip title="Seleccionar color">
                        <IconButton size="small">
                          <ColorLensIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="duracion_cita_defecto"
                  name="duracion_cita_defecto"
                  label="Duración por Defecto (min) *"
                  type="number"
                  value={formik.values.duracion_cita_defecto}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.duracion_cita_defecto && Boolean(formik.errors.duracion_cita_defecto)}
                  helperText={formik.touched.duracion_cita_defecto && formik.errors.duracion_cita_defecto}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="tarifa_por_defecto"
                  name="tarifa_por_defecto"
                  label="Tarifa por Defecto (S/) *"
                  type="number"
                  value={formik.values.tarifa_por_defecto}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.tarifa_por_defecto && Boolean(formik.errors.tarifa_por_defecto)}
                  helperText={formik.touched.tarifa_por_defecto && formik.errors.tarifa_por_defecto}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="secondary">
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <CircularProgress size={24} />
              ) : editMode ? (
                'Actualizar'
              ) : (
                'Crear'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de confirmación para eliminar */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Especialidad"
        content={`¿Estás seguro de que deseas eliminar la especialidad "${selectedEspecialidad?.nombre}"? Esta acción no se puede deshacer.`}
      />
    </Box>
  );
};

export default Especialidades;